import React, { useEffect, useRef, useState } from 'react';
import { useApi, configApiRef, fetchApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { Content, Progress } from '@backstage/core-components';

const styles = `
  .powerbi-report-container {
    height: calc(100vh - 40px);
    width: 100%;
  }
  .powerbi-report-container iframe {
    border: none;
  }
`;

interface EmbedConfig {
  accessToken: string;
  embedUrl: string;
  reportId: string;
  expiry: string;
}

export const PowerBIReportPage = ({ visible }: { visible: boolean }) => {
  const config = useApi(configApiRef);
  const { fetch: fetchApi } = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  const [embedConfig, setEmbedConfig] = useState<EmbedConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reportReady, setReportReady] = useState(false);
  const embedRef = useRef<HTMLDivElement>(null);
  const reportRef = useRef<any>(null);
  const tokenExpiryRef = useRef<number>(0);
  const consecutiveAuthFailures = useRef<number>(0);
  // Lazy-load PowerBI modules only when the dashboard is first visited
  const pbiModulesRef = useRef<Promise<[typeof import('powerbi-client'), typeof import('powerbi-models')]> | null>(null);

  useEffect(() => {
    if (visible && !pbiModulesRef.current) {
      pbiModulesRef.current = Promise.all([import('powerbi-client'), import('powerbi-models')]);
    }
  }, [visible]);

  useEffect(() => {
    let refreshTimer: ReturnType<typeof setTimeout>;

    // Not visible — don't fetch, don't schedule. Just clean up.
    if (!visible) {
      return () => clearTimeout(refreshTimer);
    }

    // Visible — check if we need a fresh token or can wait
    const fetchEmbedToken = async () => {
      try {
        // Proactively refresh Backstage identity to avoid stale tokens after idle
        await identityApi.getCredentials();

        const response = await fetchApi(
          `${backendUrl}/api/powerbi/embed-token`,
        );
        if (!response.ok) {
          const body = await response.text();
          if (response.status === 401) {
            consecutiveAuthFailures.current += 1;
            console.warn(
              `Backstage session expired (attempt ${consecutiveAuthFailures.current}). ` +
              'Report continues working until its embed token expires.',
            );
            // Report already loaded — don't show error, just retry later
            if (reportRef.current) {
              if (consecutiveAuthFailures.current >= 3) {
                setError('Your session has expired. Please refresh the page.');
              }
              refreshTimer = setTimeout(fetchEmbedToken, 60000);
              return;
            }
            window.location.reload();
            return;
          }
          throw new Error(`Failed to fetch embed token: ${body}`);
        }

        consecutiveAuthFailures.current = 0;
        setError(null);
        const data: EmbedConfig = await response.json();

        tokenExpiryRef.current = new Date(data.expiry).getTime();
        
        // If report already embedded, just refresh the token silently
        if (reportRef.current) {
          reportRef.current.setAccessToken(data.accessToken);
          console.log('Token refreshed silently on existing report');
        } else {
          setEmbedConfig(data);
        }

        // Schedule next refresh 2 minutes before expiry
        const refreshIn = Math.max(
          tokenExpiryRef.current - Date.now() - 2 * 60 * 1000,
          30000,
        );
        console.log(`Token expires at ${data.expiry}, refreshing in ${Math.round(refreshIn / 60000)} min`);
        refreshTimer = setTimeout(fetchEmbedToken, refreshIn);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        // Retry after 30s on failure so the dashboard can recover automatically
        refreshTimer = setTimeout(fetchEmbedToken, 30000);
      }
    };

    // Check if existing token is still fresh enough
    const remaining = tokenExpiryRef.current - Date.now();

    if (remaining > 2 * 60 * 1000) {
      // Token still has > 2 min left — just schedule refresh for later
      console.log(`Returning to dashboard, token still valid for ${Math.round(remaining / 60000)} min`);
      refreshTimer = setTimeout(fetchEmbedToken, remaining - 2 * 60 * 1000);
    } else {
      fetchEmbedToken();
    }

    return () => clearTimeout(refreshTimer);
  }, [visible, backendUrl, fetchApi, identityApi]);

  useEffect(() => {
    console.log('Embed config updated:', embedConfig);
    if (!embedConfig || !embedRef.current || !pbiModulesRef.current) return;

    let mounted = true;

    (async () => {
      // Use lazy-loaded modules — started downloading when visible became true
      const [pbi, pbiModels] = await pbiModulesRef.current!;
      if (!mounted || !embedRef.current) return;

      const powerbi = new pbi.service.Service(
        pbi.factories.hpmFactory,
        pbi.factories.wpmpFactory,
        pbi.factories.routerFactory,
      );

      // Reset any existing embed on this element before re-embedding
      powerbi.reset(embedRef.current);

      const report = powerbi.embed(embedRef.current, {
        type: 'report',
        id: embedConfig.reportId,
        embedUrl: embedConfig.embedUrl,
        accessToken: embedConfig.accessToken,
        tokenType: pbiModels.TokenType.Embed,
        settings: {
          panes: {
            filters: { expanded: false, visible: true },
            pageNavigation: { visible: true, position: pbiModels.PageNavigationPosition.Left },
          },
          background: pbiModels.BackgroundType.Default,
        },
      });

      reportRef.current = report;
      setReportReady(true);
    })();

    return () => { mounted = false; };
  }, [embedConfig]);

  // Refresh report visuals once every 12 hours to pick up latest dataset data
  useEffect(() => {
    const interval = setInterval(() => {
      if (reportRef.current) {
        reportRef.current.refresh().catch(() => {});
        console.log('12-hour visual refresh triggered');
      }
    }, 12 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={visible
      ? { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }
      : { display: 'none' }
    }>
      {/* <PageWithHeader title="Service Catalog Dashboard" themeId="home"> */}
        <style>{styles}</style>
        <Content>
          {!reportReady && <Progress />}
          {error && <div style={{ color: 'red', padding: '1rem' }}>Error: {error}</div>}
          <div ref={embedRef} className="powerbi-report-container" />
        </Content>
      {/* </PageWithHeader> */}
    </div>
  );
};


